import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { JwtLegFiClaims } from './auth/jwt-legfi-claims.model';
import { LegFiJwtService } from './auth/legfi-jwt.service';
import { Routes } from '../config/routes';
import { ApplicationHttpClient } from '../components/shared/http/application-http-client';
import { Observable } from 'rxjs';
import { UnifiedBankAccount } from '../models/entities/unified-bank-account';
import { GrowlerService } from '../components/shared/growler/growler.service';
import { PlaidToken } from '../models/entities/plaid-token';
import { PlaidTokenService } from './plaid-token.service';

declare var Plaid: any;

export interface PlaidSuccessMetaData
{
    institution: {
        name: string;
        institution_id: string;
    };
    account_id: string;
    account: {
        id: string;
        name: string;
        mask: string;
        type: string;
        subtype: string;
        verification_status: string;
    };
    accounts: {
        id: string;
        name: string;
        mask: string;
        type: string;
        subtype: string;
        verification_status: string;
    }[];
    linkSessionId: string;
    public_token: string;
}

export interface PlaidLinkResult
{
    success: boolean;
    publicToken?: string;
    metadata?: PlaidSuccessMetaData;
}

export interface PlaidInstitutionSearchResult
{
    countryCodes: string[];
    credentials: {
        label: string; name: string; type: string;
    }[];
    hasMfa: boolean;
    inputSpec: string;
    institutionId: string;
    mfa: string[];
    mfaCodeType: string;
    name: string;
    oauth: boolean;
    products: string[];
    routingNumbers: string[];
}

@Injectable({
    providedIn: 'root',
})
export class PlaidService
{
    private plaidObject: any = null;

    constructor(
            private _http: ApplicationHttpClient,
            private _growler: GrowlerService,
            private _plaidTokenService: PlaidTokenService,
    ) {
    }

    /**
     * The prompt copy, so a v2 surface asking the same question through a v2 dialog cannot reword
     * it. Same reasoning as the balance-CTA helpers: share the sentence, not a second copy of it.
     */
    plaidReconnectPrompt(account: UnifiedBankAccount): { title: string; content: string } {
        const loginRequired = account.plaidToken.plaidError.errorCode === 'ITEM_LOGIN_REQUIRED';

        return {
            title: loginRequired ? 'Plaid Login Required' : 'Plaid Error',
            content: loginRequired
                ? 'The login details for this connection have changed (password, MFA) and a user login is required to restore connectivity. Transaction imports have been paused. Click "Continue to Plaid" below to login and restore connectivity to your accounts.'
                : account.plaidToken.plaidError.errorMessage,
        };
    }

    /**
     * The relink itself, once the caller has confirmed. Public so a surface with its own
     * confirmation dialog does not have to reimplement the token fetch and Plaid handoff.
     */
    relaunchPlaidLink(account: UnifiedBankAccount, onSuccess: () => void): void {
        // The bank-accounts list response no longer includes updateLinkToken (SD-6302 removed it as
        // a slow accessor), so fetch a fresh token only once the user has committed to relinking.
        this._plaidTokenService.getToken(account.plaidToken.id).subscribe({
            next: (token: PlaidToken) => this.openPlaidLink(token, onSuccess),
            error: () => this._growler.oops(
                    'We were unable to reach Plaid to start the reconnection. Please try again in a few minutes.',
            ),
        });
    }

    private openPlaidLink(token: PlaidToken, onSuccess: () => void): void {
        if (!token?.updateLinkToken) {
            this._growler.oops(
                    'We were unable to reach Plaid to start the reconnection. Please try again in a few minutes.',
            );
            return;
        }
        const plaidConfig = {
            onSuccess: (publicToken: any, metadata: PlaidSuccessMetaData) => {
                this.plaidObject.destroy();
                this.plaidObject = null;
                onSuccess();
            },
            onExit: (error: any, metadata: any) => {
                console.warn('Your Plaid Link Session ID was ' + metadata.linkSessionId);
                console.warn('Please provide your Link Session ID to PayHOA Staff if requested.');
                this.plaidObject.destroy();
                if (error !== null) {
                    // an error occurred in plaid
                    this._growler.oops('An error occurred during the linking process. Please try again.');
                } else {
                    // The user exited plaid
                    this._growler.oops('You must complete the Plaid link process to enable transactions. Please try again.');
                }
            },
            token: token.updateLinkToken,
        };

        try {
            this.plaidObject = Plaid.create(plaidConfig);
            this.plaidObject.open();
        } catch (e) {
            // If create() succeeded but open() threw, destroy the orphaned instance
            // before nulling so we don't leak Plaid SDK state.
            try {
                this.plaidObject?.destroy();
            } catch {
                // intentionally swallowed — destroy() best-effort during cleanup
            }
            this.plaidObject = null;
            this._growler.oops('Could not open the Plaid reconnection window. Please try again.');
        }
    }

    /**
     * @param {string} searchTerm
     * @param context
     * @param isRoutingNumber
     * @returns {Observable<Object>}
     */
    public searchInstitutions(
            searchTerm: string,
            context: 'auth' | 'transactions',
            isRoutingNumber = false,
    ): Observable<PlaidInstitutionSearchResult[]> {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.PlaidSearch(context));

        return this._http.post(url, JSON.stringify({
            term: searchTerm,
            isRoutingNumber: isRoutingNumber,
        })).pipe(map((response: Object[]) => {
            return response.map(item => <PlaidInstitutionSearchResult>item);
        }));
    }

    public getLinkToken() {
        const jwt: JwtLegFiClaims = LegFiJwtService.read();
        if (jwt === null) {
            return this._http.redirectAndThrow401Observable();
        }

        const url: string = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.PlaidLinkToken());

        return this._http.post(url, JSON.stringify({})).pipe(map((res: any) => res.token));
    }
}
